import type {
    EditorContext,
    EditorAssign,
    OpenFiles,
    SaveFileResult,
} from '@pyranger/types';

export const isFileUnsaved = (fileName: string, openFiles: OpenFiles) => {
    return openFiles.get(fileName)?.isUnsaved ?? false;
};

export const getOpenFiles = (openFiles: OpenFiles) =>
    Array.from(openFiles.keys());

export const createFile = (input: {
    assign: EditorAssign;
    baseName: string;
    content: string;
    path: string;
    isUnsaved: boolean;
}) => {
    const { assign, baseName, content, isUnsaved, path } = input;

    assign(({ context }) => {
        const nextContext = { ...context };

        nextContext.textEditor.openFiles.set(path, {
            baseName,
            content,
            isUnsaved,
            path,
        });

        return nextContext;
    });
};

export const updateFile = (input: {
    assign: EditorAssign;
    baseName: string;
    path: string;
    content?: string;
    isUnsaved?: boolean;
}) => {
    const { assign, baseName, path, content, isUnsaved } = input;

    assign(({ context }) => {
        const nextContext = { ...context };

        const file = context.textEditor.openFiles.get(path);

        if (file) {
            file.baseName = baseName;
            file.content = content ?? file.content;
            file.isUnsaved = isUnsaved ?? file.isUnsaved;
            nextContext.textEditor.openFiles.set(path, file);
        }

        return nextContext;
    });
};

export const setCurrentFile = ({
    context,
    baseName,
    content,
    path,
}: {
    context: EditorContext;
    baseName: string;
    path: string;
    content: string;
}) => {
    const nextContext = { ...context };
    nextContext.textEditor.currentPath = path;
    nextContext.textEditor.openFiles.set(path, {
        baseName,
        content,
        isUnsaved: false,
        path,
    });

    return nextContext;
};

export const renameFile = ({
    assign,
    oldName,
    newName,
}: {
    assign: EditorAssign;
    oldName: string;
    newName: string;
}) => {
    assign(({ context }) => {
        const newOpenFiles = new Map(context.textEditor.openFiles);
        const fileData = newOpenFiles.get(oldName);

        if (fileData) {
            newOpenFiles.delete(oldName);
            newOpenFiles.set(newName, fileData);
        }

        return {
            ...context,
            textEditor: {
                ...context.textEditor,
                currentPath:
                    context.textEditor.currentPath === oldName
                        ? newName
                        : context.textEditor.currentPath,
                openFiles: newOpenFiles,
            },
        };
    });
};

export const setFileAsSaved = ({
    assign,
    path,
}: {
    assign: EditorAssign;
    path: string;
}) => {
    assign(({ context }) => {
        const nextContext = { ...context };

        const file = context.textEditor.openFiles.get(path);

        if (file) {
            nextContext.textEditor.openFiles.set(path, {
                ...file,
                isUnsaved: false,
            });
        }

        return nextContext;
    });
};

export const updateContextWithSavedFile = ({
    context,
    saveFileResult,
}: {
    context: EditorContext;
    saveFileResult: SaveFileResult;
}) => {
    const { savedPath, renamedFile } = saveFileResult;

    const nextOpenFiles = new Map(context.textEditor.openFiles);

    if (savedPath) {
        const fileData = nextOpenFiles.get(savedPath);

        if (fileData) {
            nextOpenFiles.set(savedPath, {
                ...fileData,
                isUnsaved: false,
            });
        }

        return {
            ...context,
            textEditor: {
                ...context.textEditor,
                currentPath: savedPath,
                openFiles: nextOpenFiles,
            },
        };
    }

    if (renamedFile) {
        const { oldPath, newPath } = renamedFile;

        const oldFileData = nextOpenFiles.get(oldPath);

        if (oldFileData) {
            nextOpenFiles.delete(oldPath);

            const segments = newPath
                .split('/')
                .filter((segment) => segment.length > 0);

            const nextFileData = {
                ...oldFileData,
                baseName: segments?.[segments.length - 1] ?? newPath,
                path: newPath,
                isUnsaved: false,
            };

            nextOpenFiles.set(newPath, nextFileData);

            const parent = Object.values(context.fileBrowser.items).find(
                (item) => item?.children?.includes(oldPath),
            );

            delete context.fileBrowser.items[oldPath];

            if (parent) {
                parent.children = (parent?.children ?? []).filter(
                    (child: string | number) => child !== oldPath,
                );
                context.fileBrowser.dataProvider.onDidChangeTreeDataEmitter.emit(
                    [parent.index],
                );
            }

            context.fileBrowser.treeRef?.current?.selectItems([newPath]);
        }

        return {
            ...context,
            textEditor: {
                ...context.textEditor,
                openFiles: nextOpenFiles,
                currentPath:
                    context.textEditor.currentPath === oldPath
                        ? newPath
                        : context.textEditor.currentPath,
            },
        };
    }

    return context;
};

export const removeFileFromTabs = ({
    context,
    path,
}: {
    context: EditorContext;
    path: string;
}) => {
    const newOpenFiles = new Map(context.textEditor.openFiles);
    newOpenFiles.delete(path);

    const openFilePaths = Array.from(newOpenFiles.keys());
    let newCurrentPath = context.textEditor.currentPath;

    if (path === context.textEditor.currentPath) {
        newCurrentPath =
            openFilePaths.length > 0
                ? openFilePaths[openFilePaths.length - 1]
                : null;
    }

    return {
        ...context,
        textEditor: {
            ...context.textEditor,
            currentPath: newCurrentPath,
            openFiles: newOpenFiles,
        },
    };
};
